import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { getDisplayName } from '../utils/displayHelpers';
import {
  Calendar,
  MessageCircle,
  Plus,
  Search,
  CheckCircle,
  ArrowRight,
  IndianRupee,
  Dog,
  ExternalLink,
} from 'lucide-react';
import { Icon } from '../icons';
import ScrollReveal from '../components/ui/ScrollReveal';
import ScrollRevealStack from '../components/ui/ScrollRevealStack';
import DashboardSkeleton from '../components/ui/DashboardSkeleton';
import DataFetchState from '../components/ui/DataFetchState';

const Dashboard = () => {
  const { user } = useAuth();
  const {
    pets,
    bookings,
    offlineJobs,
    checkFeatureAccess,
    userDataLoading,
    userDataError,
    refreshUserData,
  } = useData();

  const hasOfflineAccess = checkFeatureAccess(user?.id, 'offline_jobs');
  const userPets = pets.filter((pet) => pet.ownerId === user?.id);
  const userBookings = bookings.filter((booking) => booking.ownerId === user?.id);
  const userOfflineJobs = offlineJobs.filter((job) => job.userId === user?.id);

  const upcomingBookings = userBookings.filter(
    (b) => b.status === 'accepted' || b.status === 'in_progress'
  );
  const pendingBookings = userBookings.filter((b) => b.status === 'requested');

  const combinedStats = useMemo(() => {
    const platformSpend = userBookings.reduce((sum, b) => sum + (b.escrowAmount || 0), 0);
    const externalSpend = userOfflineJobs.reduce((sum, j) => sum + j.amount + (j.taxAmount || 0), 0);
    return {
      totalSpend: platformSpend + externalSpend,
      platformSpend,
      externalSpend,
      externalJobsCount: userOfflineJobs.length,
    };
  }, [userBookings, userOfflineJobs]);

  const stats = [
    { title: 'Active bookings', value: upcomingBookings.length, icon: Calendar, link: '/owner-bookings' },
    { title: 'My pets', value: userPets.length, icon: Dog, link: '/owner-pets' },
    { title: 'Completed stays', value: userBookings.filter((b) => b.status === 'completed').length, icon: CheckCircle, link: '/owner-bookings' },
    {
      title: hasOfflineAccess ? 'Total spend' : 'Total spent',
      value: `₹${combinedStats.totalSpend.toLocaleString()}`,
      icon: IndianRupee,
      link: hasOfflineAccess ? '/owner-offline-jobs' : '/owner-bookings',
    },
  ];

  const quickActions = [
    { title: 'Find caregivers', desc: 'Browse local profiles', icon: Search, link: '/search' },
    { title: 'Add pet profile', desc: 'Set up your pet\'s details', icon: Plus, link: '/owner-pets' },
    { title: 'Messages', desc: 'Chat with caregivers', icon: MessageCircle, link: '/owner-messages' },
    { title: 'My bookings', desc: 'Track all requests', icon: Calendar, link: '/owner-bookings' },
    ...(hasOfflineAccess
      ? [{ title: 'External jobs', desc: 'Track offline care', icon: ExternalLink, link: '/owner-offline-jobs', badge: combinedStats.externalJobsCount }]
      : []),
  ];

  return (
    <div className="pf-page-wrap pb-32 lg:pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome bento hero */}
        <ScrollReveal direction="left">
          <div className="pf-organic-card pf-organic-card--dark p-8 mb-8 relative overflow-hidden">
            <div className="pf-hero-v2__pattern absolute inset-0 opacity-30" />
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <span className="pf-eyebrow pf-eyebrow--on-dark mb-3">Your dashboard</span>
                <h1 className="pf-display text-3xl lg:text-4xl text-white mt-2">
                  Hello, {getDisplayName(user)}
                </h1>
                <p className="text-blue-100/80 mt-2">Here&apos;s what&apos;s happening with your pets and bookings</p>
              </div>
              <Link to="/search" className="inline-flex items-center gap-2 bg-white text-[var(--pf-accent)] px-6 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors shrink-0">
                Find a caregiver
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </ScrollReveal>

        {/* Stats bento */}
        {userDataLoading ? (
          <DashboardSkeleton statCount={stats.length} />
        ) : userDataError ? (
          <DataFetchState
            error={userDataError}
            onRetry={refreshUserData}
            errorTitle="Couldn't load your dashboard"
            compact
            className="mb-8"
          />
        ) : (
        <ScrollRevealStack className="pf-consumer-bento mb-8" staggerMs={105}>
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link key={stat.title} to={stat.link} className="pf-organic-card pf-bento-cell group">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[var(--pf-accent-soft)] flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Icon className="w-5 h-5 text-[var(--pf-accent)]" />
                  </div>
                  <div>
                    <p className="text-sm text-[var(--pf-ink-muted)]">{stat.title}</p>
                    <p className="text-2xl font-bold text-[var(--pf-ink)]">{stat.value}</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </ScrollRevealStack>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Quick actions */}
            <section className="pf-organic-card p-6">
              <ScrollReveal direction="left">
                <h2 className="font-semibold text-[var(--pf-ink)] mb-5">Quick actions</h2>
              </ScrollReveal>
              <ScrollRevealStack className="grid grid-cols-1 sm:grid-cols-2 gap-3" staggerMs={75}>
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Link key={action.title} to={action.link} className="flex items-center gap-4 p-4 rounded-xl border border-[var(--pf-border)] hover:border-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)] transition-[transform,border-color,background-color] duration-[160ms] ease-out active:scale-[0.98] group">
                      <div className="w-10 h-10 rounded-xl bg-[var(--pf-accent)] flex items-center justify-center relative shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                        {action.badge > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                            {action.badge}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[var(--pf-ink)] group-hover:text-[var(--pf-accent)]">{action.title}</p>
                        <p className="text-sm text-[var(--pf-ink-muted)] truncate">{action.desc}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[var(--pf-ink-muted)] group-hover:text-[var(--pf-accent)] shrink-0" />
                    </Link>
                  );
                })}
              </ScrollRevealStack>
            </section>

            {/* Recent bookings */}
            <ScrollReveal direction="right">
              <section className="pf-organic-card p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-[var(--pf-ink)]">Recent bookings</h2>
                <Link to="/owner-bookings" className="text-sm font-medium text-[var(--pf-accent)] inline-flex items-center gap-1">
                  View all <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {userBookings.length > 0 ? (
                <div className="space-y-3">
                  {userBookings.slice(0, 3).map((booking) => (
                    <div key={booking.id} className="p-4 rounded-xl bg-[var(--pf-canvas)] border border-[var(--pf-border)]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <img src={booking.caregiver?.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                          <div className="min-w-0">
                            <p className="font-medium text-[var(--pf-ink)] truncate">{booking.caregiver?.name}</p>
                            <p className="text-sm text-[var(--pf-ink-muted)]">{booking.pet?.name}</p>
                          </div>
                        </div>
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] capitalize shrink-0">
                          {booking.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="pf-empty-state-icon mb-3">
                    <Icon name="loader-tail-wag" size="xl" decorative />
                  </div>
                  <p className="text-[var(--pf-ink-muted)] mb-4">No bookings yet</p>
                  <Link to="/search" className="pf-btn-primary">Find caregivers</Link>
                </div>
              )}
              </section>
            </ScrollReveal>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <ScrollReveal direction="right">
              <section className="pf-organic-card p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-[var(--pf-ink)]">My pets</h2>
                <Link to="/owner-pets" className="text-sm text-[var(--pf-accent)]">Manage</Link>
              </div>
              {userPets.length > 0 ? (
                <div className="space-y-3">
                  {userPets.map((pet) => (
                    <div key={pet.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--pf-canvas)]">
                      <img src={pet.photos?.[0]} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div>
                        <p className="font-medium text-[var(--pf-ink)]">{pet.name}</p>
                        <p className="text-sm text-[var(--pf-ink-muted)]">{pet.breed}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="pf-empty-state-icon mb-2" style={{ width: '3.5rem', height: '3.5rem' }}>
                    <Icon name="loader-bouncing-ball" size="lg" decorative />
                  </div>
                  <p className="text-sm text-[var(--pf-ink-muted)] mb-3">No pets added yet</p>
                  <Link to="/owner-pets" className="pf-btn-primary text-sm">Add pet profile</Link>
                </div>
              )}
              </section>
            </ScrollReveal>

            {pendingBookings.length > 0 && (
              <ScrollReveal direction="left">
                <section className="pf-organic-card pf-organic-card--mint p-6">
                  <h2 className="font-semibold text-[var(--pf-ink)] mb-4">Pending requests</h2>
                  <div className="space-y-3">
                    {pendingBookings.map((booking) => (
                      <div key={booking.id} className="p-3 bg-[var(--pf-surface)] rounded-xl border border-[var(--pf-border)]">
                        <p className="font-medium text-sm">{booking.caregiver?.name}</p>
                        <p className="text-xs text-[var(--pf-ink-muted)]">{booking.pet?.name}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </ScrollReveal>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
